var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { translate } from '@utils/i18n';
import { getLongFormTemplates, getLongFormTemplateLibraries, updateLongFormTemplate, importLongFormTemplate } from '../services/LongFormTemplatesService';
import { namespace } from '../constants';
// ============================================================================
// Query Keys
// ============================================================================
export const LONG_FORM_TEMPLATES_QUERY_KEYS = {
    all: ['longFormTemplates'],
    list: () => ['longFormTemplates', 'list'],
    libraries: () => ['longFormTemplates', 'libraries'],
    detail: (id) => ['longFormTemplates', 'detail', id]
};
// ============================================================================
// Query Hooks
// ============================================================================
/**
 * Hook to fetch all long form templates
 * Uses React Query for caching and automatic refetching
 */
export function useLongFormTemplates() {
    return useQuery({
        queryKey: LONG_FORM_TEMPLATES_QUERY_KEYS.list(),
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            const response = yield getLongFormTemplates();
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data || [];
        }),
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 3
    });
}
/**
 * Hook to fetch long form template libraries and system types
 * Uses React Query for caching and automatic refetching
 */
export function useLongFormTemplateLibraries() {
    return useQuery({
        queryKey: LONG_FORM_TEMPLATES_QUERY_KEYS.libraries(),
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            const response = yield getLongFormTemplateLibraries();
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data || { libraries: [], systemTypes: [] };
        }),
        staleTime: 10 * 60 * 1000, // 10 minutes (libraries change rarely)
        retry: 3
    });
}
// ============================================================================
// Mutation Hooks
// ============================================================================
/**
 * Hook to update an existing long form template
 * Implements optimistic updates for instant UI feedback
 */
export function useUpdateLongFormTemplateMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ id, data }) {
            const response = yield updateLongFormTemplate(id, data);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onMutate: (_a) => __awaiter(this, [_a], void 0, function* ({ id, data }) {
            // Cancel outgoing queries to avoid overwriting optimistic update
            yield queryClient.cancelQueries({ queryKey: LONG_FORM_TEMPLATES_QUERY_KEYS.list() });
            // Snapshot previous value
            const previousTemplates = queryClient.getQueryData(LONG_FORM_TEMPLATES_QUERY_KEYS.list());
            // Optimistically update to the new value
            if (previousTemplates) {
                queryClient.setQueryData(LONG_FORM_TEMPLATES_QUERY_KEYS.list(), (old) => (old === null || old === void 0 ? void 0 : old.map((template) => (template.id === id ? Object.assign(Object.assign({}, template), data) : template))) || []);
            }
            return { previousTemplates };
        }),
        onError: (_error, _variables, context) => {
            // Rollback on error
            if (context === null || context === void 0 ? void 0 : context.previousTemplates) {
                queryClient.setQueryData(LONG_FORM_TEMPLATES_QUERY_KEYS.list(), context.previousTemplates);
            }
            toast.error(translate('detail.update_failure', { namespace }));
        },
        onSuccess: () => {
            toast.success(translate('detail.update_success', { namespace }));
        },
        onSettled: () => {
            // Always refetch after error or success to ensure consistency
            void queryClient.invalidateQueries({ queryKey: LONG_FORM_TEMPLATES_QUERY_KEYS.all });
        }
    });
}
/**
 * Hook to import a long form template from library
 * Does NOT use optimistic updates (we don't know what the template will look like until API responds)
 */
export function useImportLongFormTemplateMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (libraryId) => __awaiter(this, void 0, void 0, function* () {
            const response = yield importLongFormTemplate(libraryId);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onError: () => {
            toast.error(translate('import_form.failure', { namespace }));
        },
        onSuccess: () => {
            toast.success(translate('import_form.success', { namespace }));
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: LONG_FORM_TEMPLATES_QUERY_KEYS.all });
        }
    });
}
